import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Lock } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { api, track, errorText, isEmail } from "../lib/api";

const SECTIONS = [
  { id: "identity", title: "Confirm your identity", isPrefill: true },
  { id: "business", title: "Business Foundation", questions: [
    { k: "years_active", label: "How many years has the company operated under its current authority or ownership structure?", type: "select", options: ["0-1","1-3","3-5","5-10","10+"] },
    { k: "active_drivers_count", label: "How many active company drivers and leased-on owner-operators are currently working with the business?", type: "select", options: ["0","1","2-3","4-6","7-15","16+"] },
    { k: "freight_mix", label: "Which best describes where most of your freight comes from today?", type: "select", options: ["Direct customers","Brokers","Load boards","Dispatch service","Mixed"] },
    { k: "concentration_band", label: "Approximately how much of revenue depends on your single largest customer, broker, or freight source?", type: "select", options: ["<25%","25-50%","50-75%",">75%"] },
  ]},
  { id: "pain", title: "Pain & Urgency", questions: [
    { k: "primary_problem", label: "The single biggest financial or operational problem you need solved", type: "textarea" },
    { k: "why_now", label: "Why are you seeking help now instead of waiting six months?", type: "textarea" },
    { k: "monthly_impact_band", label: "Approximate monthly cost of this problem", type: "select", options: ["<$5K","$5K-$15K","$15K-$50K",">$50K","unknown"] },
    { k: "prior_attempts", label: "What have you already tried, and why did it not work?", type: "textarea" },
    { k: "consequence_90_day", label: "What is most likely to happen in the next 90 days if nothing changes?", type: "select", options: ["Business fails","Major cash / operational crisis","Continued erosion","Stable but stuck","Unclear"] },
    { k: "goal_90_day", label: "What measurable result would make the next 90 days feel successful?", type: "textarea" },
  ]},
  { id: "financial", title: "Financial Visibility", questions: [
    { k: "profit_by_truck_visibility", label: "How clearly can you identify profit or loss by individual truck?", type: "select", options: ["Yes","Partial","No"] },
    { k: "reports_available", label: "Which financial and operating reports can you access today?", type: "select", options: ["Full P&L + operational","P&L only","QuickBooks or similar","Basic bookkeeping","None"] },
    { k: "financial_condition", label: "Which best describes the company's immediate financial condition?", type: "select", options: ["Stable","Tight","Delayed obligations","Critical"] },
  ]},
  { id: "dependence", title: "Owner Dependence", questions: [
    { k: "owner_dependency", label: "What stops or becomes chaotic when you are unavailable?", type: "select", options: ["Everything stops","Major operations slow","Some slowdown","Business runs fine"] },
    { k: "weekly_role", label: "Which best describes your current weekly role?", type: "select", options: ["Driving","Dispatching","Managing","Strategic","Absent owner"] },
  ]},
  { id: "readiness", title: "Readiness", questions: [
    { k: "document_readiness", label: "Are you prepared to provide accurate financial and operating documents promptly?", type: "select", options: ["Yes","With time","No"] },
    { k: "change_readiness", label: "Willing to change processes, vendors, or personal operating habits when the evidence supports it?", type: "select", options: ["Yes","Selectively","No"] },
    { k: "authority_investment_readiness", label: "Which statement best describes your authority and readiness if a paid Driver Reborn Advisory engagement is the responsible next step?", type: "select", options: [
      "I am the final decision-maker and can begin now.",
      "I am the final decision-maker and can begin within 30 days.",
      "Another decision-maker must approve and will attend the call.",
      "I am authorized but would need an approved payment arrangement.",
      "I am gathering information and am not prepared to invest.",
      "I am not authorized to approve the engagement.",
    ]},
  ]},
];

const emptyForm = () => {
  const f = {
    first_name: "", email: "", phone: "", company: "", state: "",
    active_trucks: "", annual_revenue: "", assessment_result_id: "",
  };
  SECTIONS.forEach(s => (s.questions || []).forEach(q => f[q.k] = ""));
  return f;
};

function applyButtonLabel({ submitting, section, qIdx, sectionsLen, questionsLen }) {
  if (submitting) return "Submitting…";
  const isFinalQuestion = section === sectionsLen - 1 && qIdx === questionsLen - 1;
  const label = isFinalQuestion ? "Submit My Request" : "Continue";
  return <>{label} <ArrowRight size={16}/></>;
}

export default function Apply() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [section, setSection] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [prefilled, setPrefilled] = useState(false);

  // Prefill from assessment result if ?rid= present
  // `api`/`track` are stable module imports; guarded by `prefilled`
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const rid = params.get("rid");
    if (!rid || prefilled) return;
    api.get(`/assessment/result/${rid}`).then(r => {
      setForm(prev => ({ ...prev, assessment_result_id: rid }));
      setPrefilled(true);
    }).catch(() => setPrefilled(true));
    track("application_start", "/apply", { rid });
  }, [params, prefilled]);

  // `track` is a stable module import; fire once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { track("application_start", "/apply"); }, []);

  const totalQuestions = SECTIONS.slice(1).reduce((n, s) => n + s.questions.length, 0);
  const answered = SECTIONS.slice(1, section + 1).reduce((n, s, i) =>
    n + (i === section - 1 ? qIdx : (s.questions?.length || 0)), 0);
  const progress = section === 0 ? 5 : Math.round(((answered + 1) / (totalQuestions + 1)) * 100);

  const cur = SECTIONS[section];
  const curQ = cur.questions ? cur.questions[qIdx] : null;

  const advance = () => {
    setError("");
    if (curQ && !form[curQ.k]) { setError(`Please answer: ${curQ.label}`); return; }
    if (cur.questions && qIdx < cur.questions.length - 1) {
      setQIdx(qIdx + 1);
    } else if (section < SECTIONS.length - 1) {
      setSection(section + 1); setQIdx(0);
    } else {
      submit();
    }
    track("application_step_complete", "/apply", { section: cur.id });
  };

  const back = () => {
    setError("");
    if (qIdx > 0) setQIdx(qIdx - 1);
    else if (section > 0) {
      const prev = SECTIONS[section - 1];
      setSection(section - 1);
      setQIdx(prev.questions ? prev.questions.length - 1 : 0);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/strategy-call/application", form);
      track("application_submit", "/apply", { lane: res.data.lane, score: res.data.score });
      const state = res.data.thank_you_state;
      nav(`/thank-you/${state}${state === "qualified" && res.data.calendar_url ? `?url=${encodeURIComponent(res.data.calendar_url)}` : ""}`);
    } catch (e) {
      setError(errorText(e, "Submission failed. Try again."));
      setSubmitting(false);
    }
  };

  const identityFields = [
    { k: "first_name", label: "First name", required: true },
    { k: "email", label: "Business email", type: "email", required: true },
    { k: "phone", label: "Mobile (optional)", type: "tel" },
    { k: "company", label: "Company", required: true },
    { k: "state", label: "State", required: false },
    { k: "active_trucks", label: "Active trucks", type: "select", options: ["1","2-3","4-5","5-10","11-20","20+"], required: true },
    { k: "annual_revenue", label: "Annual revenue", type: "select", options: ["Under $500K","$500K-$1M","$1M-$2M","$2M-$5M","Over $5M"], required: true },
  ];

  const identityValid = identityFields.filter(f => f.required).every(f => form[f.k]);

  return (
    <Layout minimal>
      <SEO title="Request a 20-Minute Fleet Owner Conversation | Driver Reborn Advisory" description="Request a private 20-minute Fleet Owner Conversation with Brandon Winston of Driver Reborn Advisory." />
      <section className="section">
        <div className="container-tight">
          <div className="dr-progress-track mb-8" data-testid="apply-progress">
            <div className="dr-progress-fill" style={{ width: `${progress}%` }}/>
          </div>
          <div className="flex items-center gap-2 text-[#FF6A00] text-xs uppercase tracking-widest mb-2">
            <Lock size={12}/> About 7-9 minutes. Section {section + 1} of {SECTIONS.length}.
          </div>

          {section === 0 && (
            <div>
              <h1 className="font-bebas text-4xl sm:text-5xl uppercase leading-none mb-2" data-testid="apply-identity-title">
                Request a 20-Minute Fleet Owner Conversation
              </h1>
              <p className="text-[#A1A1AA] mb-6">
                {prefilled ? "We linked your Fleet Crossroads Scorecard result. Confirm or edit below." : "Confirm the essentials so we do not re-ask them later."}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {identityFields.map(f => (
                  <div key={f.k} className={f.k === "company" || f.k === "state" ? "sm:col-span-1" : "sm:col-span-2"}>
                    <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">{f.label}</label>
                    {f.type === "select" ? (
                      <select required={f.required} value={form[f.k]} onChange={e=>setForm({...form, [f.k]: e.target.value})}
                        data-testid={`apply-${f.k}`}
                        className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none">
                        <option value="">Select</option>
                        {f.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type || "text"} required={f.required} value={form[f.k]}
                        onChange={e=>setForm({...form, [f.k]: e.target.value})}
                        data-testid={`apply-${f.k}`}
                        className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
                    )}
                  </div>
                ))}
              </div>
              {error && <div className="text-[#EF4444] text-sm mb-4" data-testid="apply-identity-error">{error}</div>}
              <button onClick={()=>{
                if (!identityValid) { setError("Please fill in all required fields."); return; }
                if (!isEmail(form.email)) { setError("Please enter a valid business email."); return; }
                setSection(1); setError("");
              }}
                className="btn-primary" data-testid="apply-identity-continue">
                Continue <ArrowRight size={16}/>
              </button>
            </div>
          )}

          {section > 0 && curQ && (
            <div>
              <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2" data-testid={`apply-section-${cur.id}`}>
                {cur.title}
              </div>
              <h2 className="font-bebas text-3xl sm:text-4xl uppercase leading-tight mb-6" data-testid={`apply-q-${curQ.k}`}>
                {curQ.label}
              </h2>
              {curQ.type === "select" ? (
                <div className="grid gap-3">
                  {curQ.options.map((o) => (
                    <button key={o} onClick={()=>setForm({...form, [curQ.k]: o})}
                      data-testid={`apply-opt-${curQ.k}-${o.replace(/\s+/g,'-').slice(0,40)}`}
                      className={`text-left border ${form[curQ.k] === o ? "border-[#FF6A00] text-[#FF6A00]" : "border-[#2B2B2B] text-[#E6E6E6]"} bg-[#111111] hover:border-[#FF6A00] px-5 py-4 font-inter text-sm transition-colors`}>
                      {o}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea rows={4} value={form[curQ.k]} onChange={e=>setForm({...form, [curQ.k]: e.target.value})}
                  data-testid={`apply-input-${curQ.k}`}
                  className="w-full bg-[#2B2B2B] border border-[#2B2B2B] text-[#E6E6E6] px-4 py-3 focus:border-[#FF6A00] outline-none"/>
              )}
              {error && <div className="text-[#EF4444] text-sm mt-4" data-testid="apply-error">{error}</div>}
              <div className="flex justify-between mt-6 gap-4">
                <button onClick={back} className="btn-ghost" data-testid="apply-back">
                  <ArrowLeft size={14}/> Back
                </button>
                <button onClick={advance} disabled={submitting} className="btn-primary" data-testid="apply-next">
                  {applyButtonLabel({ submitting, section, qIdx, sectionsLen: SECTIONS.length, questionsLen: cur.questions.length })}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
